import React, { type FunctionComponent } from 'react'
import { type FormContextValues } from 'react-hook-form'
import { type CollectionsFormData } from 'domains/collections/components/collections-form/utils/collectionsForm.types'
import { CollectionsFormSection } from 'domains/collections/components/collections-form/components/form-section/CollectionsFormSection'
import { CollectionsFormImageInput } from 'domains/collections/components/collections-form/components/form-image/CollectionsFormImageInput'
import { CollectionsFormColorInput } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormColorInput'
import { CollectionsFormInput } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormInput'
import { CollectionsFormCheckbox } from 'domains/collections/components/collections-form/components/form-input/CollectionsFormCheckbox'
import { collectionsFormTestIds } from 'domains/collections/components/collections-form/utils/collectionsForm.utils'
import { CollectionsFormField } from 'domains/collections/components/collections-form/components/form-field/CollectionsFormField'
import { CollectionsFormRow } from 'domains/collections/components/collections-form/components/form-row/CollectionsFormRow'
import { collectionStorefrontBannerOptions } from 'domains/collections/components/collections-form/utils/collectionFormImageOptions'

interface CollectionsStorefrontBannerProps {
  register: FormContextValues<CollectionsFormData>['register']
  errors: FormContextValues<CollectionsFormData>['errors']
  getValues: FormContextValues<CollectionsFormData>['getValues']
  control: FormContextValues<CollectionsFormData>['control']
  showFormFragment: boolean
}

export const CollectionsStorefrontBanner: FunctionComponent<CollectionsStorefrontBannerProps> = ({
  register,
  errors,
  getValues,
  control,
  showFormFragment,
}) => {
  const collectionStorefrontBannerContent = (
    <React.Fragment>
      <CollectionsFormRow>
        <CollectionsFormCheckbox
          name="storefrontBanner.active"
          identifier="storefrontBanner.active"
          control={control}
          labelId="collectionsDomain.collectionsForm.section.storefrontBanner.display"
          data-testid={collectionsFormTestIds.storefrontBannerActiveField}
        />
      </CollectionsFormRow>

      <CollectionsFormRow>
        <CollectionsFormField>
          <CollectionsFormInput
            identifier="storefrontBanner.heading"
            labelId="collectionsDomain.collectionsForm.section.storefrontBanner.heading"
            errors={errors}
            errorMessage="collectionsDomain.collectionsForm.section.storefrontBanner.heading.error.required"
            ref={register({ required: true })}
            data-testid={collectionsFormTestIds.storefrontBannerHeadingField}
          />
        </CollectionsFormField>

        <CollectionsFormField>
          <CollectionsFormColorInput
            identifier="storefrontBanner.headingColor"
            labelId="collectionsDomain.collectionsForm.section.storefrontBanner.headingColor"
            defaultValue={getValues('storefrontBanner.headingColor')}
            ref={register({ required: true })}
            errorMessage="collectionsDomain.collectionsForm.section.storefrontBanner.headingColor.error.required"
            errors={errors}
            data-testid={collectionsFormTestIds.storefrontBannerHeadingColorField}
          />
        </CollectionsFormField>
      </CollectionsFormRow>

      <CollectionsFormRow>
        <CollectionsFormField>
          <CollectionsFormInput
            identifier="storefrontBanner.subHeading"
            labelId="collectionsDomain.collectionsForm.section.storefrontBanner.subHeading"
            errors={errors}
            errorMessage="collectionsDomain.collectionsForm.section.storefrontBanner.subHeading.error.required"
            ref={register({ required: true })}
            data-testid={collectionsFormTestIds.storefrontBannerSubHeadingField}
          />
        </CollectionsFormField>

        <CollectionsFormField>
          <CollectionsFormColorInput
            identifier="storefrontBanner.subHeadingColor"
            labelId="collectionsDomain.collectionsForm.section.storefrontBanner.subHeadingColor"
            defaultValue={getValues('storefrontBanner.subHeadingColor')}
            ref={register({ required: true })}
            errorMessage="collectionsDomain.collectionsForm.section.storefrontBanner.subHeadingColor.error.required"
            errors={errors}
            data-testid={collectionsFormTestIds.storefrontBannerSubHeadingColorField}
          />
        </CollectionsFormField>
      </CollectionsFormRow>

      <CollectionsFormRow>
        <CollectionsFormField>
          <CollectionsFormInput
            identifier="storefrontBanner.tag"
            labelId="collectionsDomain.collectionsForm.section.storefrontBanner.tag"
            placeholderMessageId="collectionsDomain.collectionsForm.section.storefrontBanner.tag.placeholder"
            errors={errors}
            ref={register({ required: false })}
            data-testid={collectionsFormTestIds.storefrontBannerTagField}
          />
        </CollectionsFormField>

        <CollectionsFormField>
          <CollectionsFormColorInput
            identifier="storefrontBanner.tagColor"
            labelId="collectionsDomain.collectionsForm.section.storefrontBanner.tagColor"
            placeholderMessageId="collectionsDomain.collectionsForm.section.storefrontBanner.tagColor.placeholder"
            defaultValue={getValues('storefrontBanner.tagColor')}
            ref={register({ required: false })}
            errors={errors}
            data-testid={collectionsFormTestIds.storefrontBannerTagColorField}
          />
        </CollectionsFormField>
      </CollectionsFormRow>

      <CollectionsFormRow>
        <CollectionsFormField>
          <CollectionsFormInput
            identifier="storefrontBanner.cta"
            labelId="collectionsDomain.collectionsForm.section.storefrontBanner.cta"
            errorMessage="collectionsDomain.collectionsForm.section.storefrontBanner.cta.error.required"
            errors={errors}
            ref={register({ required: true })}
            data-testid={collectionsFormTestIds.storefrontBannerCTAField}
          />
        </CollectionsFormField>

        <CollectionsFormField>
          <CollectionsFormColorInput
            identifier="storefrontBanner.backgroundColor"
            labelId="collectionsDomain.collectionsForm.section.storefrontBanner.backgroundColor"
            placeholderMessageId="collectionsDomain.collectionsForm.section.storefrontBanner.backgroundColor.placeholder"
            defaultValue={getValues('storefrontBanner.backgroundColor')}
            ref={register({ required: false })}
            errors={errors}
            data-testid={collectionsFormTestIds.storefrontBannerBackgroundColorField}
          />
        </CollectionsFormField>
      </CollectionsFormRow>

      <CollectionsFormImageInput
        {...collectionStorefrontBannerOptions}
        ref={register({ required: true })}
        errors={errors}
        data-testid={collectionsFormTestIds.storefrontBannerImageField}
        defaultUrl={getValues(collectionStorefrontBannerOptions.identifier)}
      />
    </React.Fragment>
  )

  return (
    <CollectionsFormSection titleId="collectionsDomain.collectionsForm.section.storefrontBanner">
      <CollectionsFormRow>
        <CollectionsFormCheckbox
          name="showStorefrontBannerFragment"
          identifier="showStorefrontBannerFragment"
          labelId="collectionsDomain.collectionsForm.section.storefrontBanner.toggleLabel"
          control={control}
          data-testid={collectionsFormTestIds.storefrontBannerToggle}
        />
      </CollectionsFormRow>

      {showFormFragment && collectionStorefrontBannerContent}
    </CollectionsFormSection>
  )
}
